import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Loader2Icon } from 'lucide-react';

const buttonVariants = cva(
  'inline-flex items-center justify-center  shadow-[0_1px_2px_0_rgba(20,21,26,0.05)] gap-2 whitespace-nowrap rounded-[100px] text-sm font-medium transition-colors outline-none  disabled:pointer-events-none disabled:opacity-60 disabled:grayscale',
  {
    variants: {
      variant: {
        default: 'bg-green text-black shadow-xs hover:bg-green/70 ',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/50',
        outline:
          'border border-gray-smoke bg-background shadow-xs text-midnight hover:bg-accent hover:text-dark-blue',
        secondary: 'bg-muted text-foreground shadow-xs hover:bg-muted/70',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        success: 'bg-green text-white hover:bg-green/70',
        link: 'underline bg-transparent text-foreground hover:text-foreground/70 shadow-none',
        icon: 'bg-transparent outline-none shadow-none border-none !p-0',
      },
      size: {
        default: 'px-3 py-2.5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      isLoading?: boolean;
    }
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = isLoading || disabled;

    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={isDisabled}
      >
        {isLoading && <Loader2Icon className="animate-spin" />}
        {children}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
